import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  User, Search, FileText, RefreshCw, Link2, Shield, Mail,
  CheckCircle, XCircle, AlertTriangle, Clock, Ban, Trash2,
  MessageSquare, Bot, ClipboardList, BarChart2, ArrowLeft,
  Info, RotateCcw, ChevronDown, ChevronUp, X,
} from 'lucide-react';
import axios from 'axios';
import {
  getInvestor,
  getInvestorStatus,
  updateInvestorStatus,
  sendInvestorInvite,
  getSuitabilityTranscript,
  getAmlRating,
  runAmlAnalysis,
  overrideSuitability,
  getInvestorReportUrl,
} from '../api';
import { useMobile } from '../components/Layout';
import { useTheme } from '../ThemeContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const isRestricted = (val) => val === '*** RESTRICTED ***';

const statusMeta = {
  PENDING:                 { color: '#475569', bg: '#F8FAFC', border: '#E2E8F0', label: 'Pending' },
  KYC_IN_PROGRESS:         { color: '#1D4ED8', bg: '#F8FAFC', border: '#DBEAFE', label: 'KYC In Progress' },
  SUITABILITY_IN_PROGRESS: { color: '#475569', bg: '#F8FAFC', border: '#E2E8F0', label: 'Suitability In Progress' },
  DOCUMENTS_PENDING:       { color: '#92400E', bg: '#FFFBEB', border: '#FDE68A', label: 'Documents Pending' },
  ACTIVE:                  { color: '#166534', bg: '#F8FAFC', border: '#DCFCE7', label: 'Active' },
  SUSPENDED:               { color: '#991B1B', bg: '#FEF2F2', border: '#FECACA', label: 'Suspended' },
  REJECTED:                { color: '#991B1B', bg: '#FEF2F2', border: '#FECACA', label: 'Rejected' },
  GDPR_ERASED:             { color: '#64748B', bg: '#F8FAFC', border: '#E2E8F0', label: 'GDPR Erased' },
};

const resultMeta = {
  SUITABLE:   { color: '#166534', bg: '#F8FAFC', border: '#DCFCE7', label: 'Suitable' },
  UNSUITABLE: { color: '#991B1B', bg: '#FEF2F2', border: '#FECACA', label: 'Unsuitable' },
  BORDERLINE: { color: '#92400E', bg: '#FFFBEB', border: '#FDE68A', label: 'Borderline' },
};

const actionMeta = {
  PROCEED:                { color: '#166534', bg: '#F8FAFC', border: '#DCFCE7' },
  LEGAL_ALERT:            { color: '#991B1B', bg: '#FEF2F2', border: '#FECACA' },
  MANUAL_REVIEW:          { color: '#92400E', bg: '#FFFBEB', border: '#FDE68A' },
  ENHANCED_DUE_DILIGENCE: { color: '#92400E', bg: '#FFFBEB', border: '#FDE68A' },
  ESCALATE_TO_MLRO:       { color: '#991B1B', bg: '#FEF2F2', border: '#FECACA' },
};

const RestrictedBadge = () => (
  <span style={{
    fontSize: 11,
    color: '#64748B',
    background: '#F8FAFC',
    border: '1px solid #E2E8F0',
    padding: '3px 8px',
    borderRadius: 999,
    fontWeight: 600,
  }}>
    Restricted
  </span>
);

const InvestorDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isMobile = useMobile();
  const { theme } = useTheme();

  const [investor, setInvestor] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [updating, setUpdating] = useState(false);
  const [updateMsg, setUpdateMsg] = useState(null);
  const [inviting, setInviting] = useState(false);
  const [inviteMsg, setInviteMsg] = useState(null);

  const [transcript, setTranscript] = useState(null);
  const [showTranscript, setShowTranscript] = useState(false);

  const [amlRating, setAmlRating] = useState(null);
  const [amlRunning, setAmlRunning] = useState(false);

  const [showReasoning, setShowReasoning] = useState(false);
  const [showNarrative, setShowNarrative] = useState(false);

  const [showOverride, setShowOverride] = useState(false);
  const [overrideResult, setOverrideResult] = useState('SUITABLE');
  const [overrideReason, setOverrideReason] = useState('');
  const [overriding, setOverriding] = useState(false);
  const [overrideMsg, setOverrideMsg] = useState(null);

  const [deliveries, setDeliveries] = useState([]);
  const [delivering, setDelivering] = useState(false);
  const [deliverMsg, setDeliverMsg] = useState(null);

  const [showSuspend, setShowSuspend] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  const [suspending, setSuspending] = useState(false);
  const [suspendMsg, setSuspendMsg] = useState(null);

  const [showGdpr, setShowGdpr] = useState(false);
  const [gdprConfirm, setGdprConfirm] = useState('');
  const [gdprLoading, setGdprLoading] = useState(false);
  const [gdprMsg, setGdprMsg] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [invRes, statusRes] = await Promise.all([
          getInvestor(id),
          getInvestorStatus(id),
        ]);

        setInvestor(invRes.data);
        setStatus(statusRes.data);

        try {
          const amlRes = await getAmlRating(id);
          setAmlRating(amlRes.data);
        } catch {
          setAmlRating(null);
        }

        try {
          const token = localStorage.getItem('token');
          const delRes = await axios.get(`${API_URL}/communications/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setDeliveries(delRes.data.deliveries || []);
        } catch {
          setDeliveries([]);
        }
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load investor');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [id]);

  if (loading) {
    return (
      <div style={{ paddingTop: 120, textAlign: 'center', color: theme.textSecondary, fontSize: 14 }}>
        Loading investor...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ paddingTop: 120, textAlign: 'center', color: '#991B1B', fontSize: 14 }}>
        {error}
      </div>
    );
  }

  const inv = investor?.investor || investor;
  const audit = status?.audit_trail || [];
  const kyc = status?.kyc;
  const suitability = status?.suitability;
  const current = inv?.onboarding_status;

  const steps = [
    { key: 'PENDING', label: 'Pending' },
    { key: 'KYC_IN_PROGRESS', label: 'KYC' },
    { key: 'SUITABILITY_IN_PROGRESS', label: 'Suitability' },
    { key: 'DOCUMENTS_PENDING', label: 'Documents' },
    { key: 'ACTIVE', label: 'Active' },
  ];

  const validTransitions = {
    PENDING:                 ['KYC_IN_PROGRESS', 'REJECTED'],
    KYC_IN_PROGRESS:         ['SUITABILITY_IN_PROGRESS', 'REJECTED', 'SUSPENDED'],
    SUITABILITY_IN_PROGRESS: ['DOCUMENTS_PENDING', 'REJECTED', 'SUSPENDED'],
    DOCUMENTS_PENDING:       ['ACTIVE', 'REJECTED', 'SUSPENDED'],
    ACTIVE:                  ['SUSPENDED'],
    SUSPENDED:               ['ACTIVE', 'REJECTED'],
    REJECTED:                [],
  };

  const nextStatuses = validTransitions[current] || [];
  const currentStepIdx = steps.findIndex(s => s.key === current);

  const handleStatusChange = async (newStatus) => {
    setUpdating(true);
    setUpdateMsg(null);
    try {
      await updateInvestorStatus(inv.id, { status: newStatus });
      setUpdateMsg({ type: 'success', text: `Status updated to ${newStatus.replace(/_/g, ' ')}` });
      const [i, s] = await Promise.all([getInvestor(inv.id), getInvestorStatus(inv.id)]);
      setInvestor(i.data);
      setStatus(s.data);
    } catch (err) {
      setUpdateMsg({ type: 'error', text: err.response?.data?.error || 'Update failed' });
    } finally {
      setUpdating(false);
    }
  };

  const handleSendInvite = async () => {
    setInviting(true);
    setInviteMsg(null);
    try {
      await sendInvestorInvite(inv.id);
      setInviteMsg({ type: 'success', text: 'Invite sent successfully' });
    } catch (err) {
      const data = err.response?.data;
      if (data?.invite_sent_at) {
        const d = new Date(data.invite_sent_at).toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        });
        setInviteMsg({ type: 'warn', text: `Invite already sent on ${d}` });
      } else {
        setInviteMsg({ type: 'error', text: data?.error || 'Failed to send invite' });
      }
    } finally {
      setInviting(false);
    }
  };

  const handleRunAml = async () => {
    setAmlRunning(true);
    try {
      const res = await runAmlAnalysis(inv.id);
      setAmlRating(res.data);
    } catch {
      alert('AML analysis failed. Please try again.');
    } finally {
      setAmlRunning(false);
    }
  };

  const fetchTranscript = async () => {
    try {
      const res = await getSuitabilityTranscript(inv.id);
      setTranscript(res.data);
      setShowTranscript(true);
    } catch {
      alert('No conversation transcript found.');
    }
  };

  const handleOverride = async () => {
    if (overrideReason.trim().length < 20) {
      setOverrideMsg({ type: 'error', text: 'Reason must be at least 20 characters.' });
      return;
    }

    setOverriding(true);
    setOverrideMsg(null);
    try {
      await overrideSuitability(suitability.assessment_id, {
        new_result: overrideResult,
        reason: overrideReason.trim(),
      });

      setOverrideMsg({ type: 'success', text: 'Override recorded successfully.' });

      const [invRes, statusRes] = await Promise.all([
        getInvestor(inv.id),
        getInvestorStatus(inv.id),
      ]);

      setInvestor(invRes.data);
      setStatus(statusRes.data);

      setTimeout(() => {
        setShowOverride(false);
        setOverrideMsg(null);
        setOverrideReason('');
      }, 1500);
    } catch (err) {
      setOverrideMsg({ type: 'error', text: err.response?.data?.error || 'Override failed.' });
    } finally {
      setOverriding(false);
    }
  };

  const handleSuspend = async () => {
    if (suspendReason.trim().length < 10) {
      setSuspendMsg({ type: 'error', text: 'Reason must be at least 10 characters.' });
      return;
    }

    setSuspending(true);
    setSuspendMsg(null);
    try {
      await updateInvestorStatus(inv.id, {
        status: 'SUSPENDED',
        notes: suspendReason.trim(),
      });

      setSuspendMsg({ type: 'success', text: 'Investor suspended successfully.' });

      const [i, s] = await Promise.all([getInvestor(inv.id), getInvestorStatus(inv.id)]);
      setInvestor(i.data);
      setStatus(s.data);

      setTimeout(() => {
        setShowSuspend(false);
        setSuspendReason('');
        setSuspendMsg(null);
      }, 1500);
    } catch (err) {
      setSuspendMsg({ type: 'error', text: err.response?.data?.error || 'Failed to suspend.' });
    } finally {
      setSuspending(false);
    }
  };

  const handleReactivate = async () => {
    setSuspending(true);
    try {
      await updateInvestorStatus(inv.id, {
        status: 'ACTIVE',
        notes: 'Reactivated by compliance officer',
      });

      const [i, s] = await Promise.all([getInvestor(inv.id), getInvestorStatus(inv.id)]);
      setInvestor(i.data);
      setStatus(s.data);
    } catch (err) {
      setSuspendMsg({ type: 'error', text: err.response?.data?.error || 'Failed to reactivate.' });
    } finally {
      setSuspending(false);
    }
  };

  const handleGdprErasure = async () => {
    if (gdprConfirm !== inv.pseudo_id) {
      setGdprMsg({ type: 'error', text: 'Pseudo ID does not match.' });
      return;
    }

    setGdprLoading(true);
    setGdprMsg(null);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/investors/${inv.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setGdprMsg({ type: 'success', text: 'GDPR erasure complete. Redirecting...' });
      setTimeout(() => navigate('/investors'), 2000);
    } catch (err) {
      setGdprMsg({ type: 'error', text: err.response?.data?.error || 'Erasure failed.' });
    } finally {
      setGdprLoading(false);
    }
  };

  const handleSendDocuments = async () => {
    setDelivering(true);
    setDeliverMsg(null);
    try {
      const token = localStorage.getItem('token');

      const res = await axios.post(`${API_URL}/communications/deliver/${inv.id}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setDeliverMsg({ type: 'success', text: res.data.message });

      const delRes = await axios.get(`${API_URL}/communications/${inv.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setDeliveries(delRes.data.deliveries || []);
    } catch (err) {
      setDeliverMsg({ type: 'error', text: err.response?.data?.error || 'Failed to send documents.' });
    } finally {
      setDelivering(false);
    }
  };

  const shapScore = (() => {
    if (!suitability?.shap_values) return null;
    const s = suitability.shap_values;
    const pos =
      (s.knowledge_and_experience || 0) +
      (s.financial_situation || 0) +
      (s.investment_objectives || 0) +
      (s.esg_alignment || 0);
    const pen = Math.abs(s.inconsistency_penalty || 0);
    return Math.round(Math.max(0, Math.min(100, (pos - pen) * 100)));
  })();

  const Card = ({ children, style = {} }) => (
    <div style={{
      background: theme.bgCard,
      borderRadius: 10,
      border: `1px solid ${theme.border}`,
      boxShadow: theme.shadowSm,
      marginBottom: 14,
      ...style,
    }}>
      {children}
    </div>
  );

  const CardHeader = ({ icon: Icon, title, action }) => (
    <div style={{
      padding: isMobile ? '14px 16px' : '15px 20px',
      borderBottom: `1px solid ${theme.border}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: 8,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {Icon && <Icon size={15} color={theme.textSecondary} strokeWidth={2} />}
        <span style={{ fontSize: 13, fontWeight: 600, color: theme.textPrimary }}>
          {title}
        </span>
      </div>
      {action}
    </div>
  );

  const CardBody = ({ children, style = {} }) => (
    <div style={{ padding: isMobile ? '14px 16px' : '16px 20px', ...style }}>
      {children}
    </div>
  );

  const Field = ({ label, value }) => {
    const restricted = value === '*** RESTRICTED ***';

    return (
      <div>
        <p style={{
          margin: '0 0 4px',
          fontSize: 11,
          color: theme.textTertiary,
          fontWeight: 600,
        }}>
          {label}
        </p>
        <p style={{
          margin: 0,
          fontSize: 13,
          color: restricted ? theme.textTertiary : value ? theme.textPrimary : theme.textTertiary,
          fontWeight: 500,
        }}>
          {restricted ? 'Restricted' : value || '-'}
        </p>
      </div>
    );
  };

  const StatusPill = ({ value, colorMap }) => {
    const meta = colorMap?.[value] || {
      color: theme.textSecondary,
      bg: theme.bgInput,
      border: theme.border,
      label: value,
    };

    return (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        background: meta.bg,
        color: meta.color,
        border: `1px solid ${meta.border || theme.border}`,
        padding: '3px 9px',
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 600,
        whiteSpace: 'nowrap',
      }}>
        {meta.label || value}
      </span>
    );
  };

  const Btn = ({ onClick, disabled, children, variant = 'secondary', size = 'sm' }) => {
    const pad = size === 'sm' ? '7px 12px' : '9px 18px';
    const danger = variant === 'danger';
    const primary = variant === 'primary';

    return (
      <button onClick={onClick} disabled={disabled} style={{
        padding: pad,
        background: disabled ? theme.bgBadge : primary ? '#2563EB' : theme.bgInput,
        color: disabled ? theme.textTertiary : primary ? 'white' : danger ? '#991B1B' : theme.textSecondary,
        border: `1px solid ${danger ? '#FECACA' : primary ? '#2563EB' : theme.border}`,
        borderRadius: 8,
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontSize: 12,
        fontWeight: 600,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
      }}>
        {children}
      </button>
    );
  };

  const inputStyle = {
    width: '100%',
    padding: '9px 12px',
    borderRadius: 8,
    border: `1px solid ${theme.border}`,
    fontSize: 13,
    color: theme.textPrimary,
    background: theme.bgInput,
    boxSizing: 'border-box',
    outline: 'none',
  };

  return (
    <div>
      <button onClick={() => navigate('/investors')} style={{
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        color: theme.textSecondary,
        fontSize: 12,
        fontWeight: 600,
        display: 'flex',
        alignItems: 'center',
        gap: 5,
        marginBottom: 16,
        padding: 0,
      }}>
        <ArrowLeft size={13} /> Back to Investors
      </button>

      <div style={{
        background: theme.bgCard,
        borderRadius: 10,
        padding: isMobile ? '18px 16px' : '22px 24px',
        marginBottom: 14,
        border: `1px solid ${theme.border}`,
        boxShadow: theme.shadowSm,
      }}>
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 16,
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{
              color: theme.textTertiary,
              margin: '0 0 6px',
              fontSize: 11,
              fontWeight: 600,
              textTransform: 'uppercase',
            }}>
              Investor Profile
            </p>

            <h1 style={{
              color: theme.textPrimary,
              margin: '0 0 4px',
              fontSize: isMobile ? 19 : 23,
              fontWeight: 700,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {isRestricted(inv?.full_name)
                ? <RestrictedBadge />
                : inv?.full_name || <span style={{ color: theme.textTertiary, fontWeight: 400 }}>Name erased</span>}
            </h1>

            <p style={{
              color: theme.textSecondary,
              margin: '0 0 12px',
              fontSize: 12,
              fontFamily: 'monospace',
            }}>
              {inv?.pseudo_id}
            </p>

            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {inv?.fund_name && <StatusPill value={inv.fund_name} />}
              {inv?.fund_manager_name && <StatusPill value={inv.fund_manager_name} />}
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <StatusPill value={current} colorMap={statusMeta} />
            <Btn onClick={handleSendInvite} disabled={inviting} variant="primary">
              {inviting ? <><Clock size={12} />Sending...</> : <><Mail size={12} />Send Invite</>}
            </Btn>
            <a href={getInvestorReportUrl(inv.id)} target="_blank" rel="noreferrer" style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '7px 12px',
              borderRadius: 8,
              background: theme.bgInput,
              color: theme.textSecondary,
              textDecoration: 'none',
              fontSize: 12,
              fontWeight: 600,
              border: `1px solid ${theme.border}`,
            }}>
              <FileText size={12} /> Export PDF
            </a>
          </div>
        </div>

        {inviteMsg && (
          <div style={{
            marginTop: 12,
            padding: '9px 12px',
            borderRadius: 8,
            background: inviteMsg.type === 'success' ? '#F0FDF4' : inviteMsg.type === 'warn' ? '#FFFBEB' : '#FEF2F2',
            border: `1px solid ${inviteMsg.type === 'success' ? '#DCFCE7' : inviteMsg.type === 'warn' ? '#FDE68A' : '#FECACA'}`,
            color: inviteMsg.type === 'success' ? '#166534' : inviteMsg.type === 'warn' ? '#92400E' : '#991B1B',
            fontSize: 12,
            fontWeight: 600,
          }}>
            {inviteMsg.text}
          </div>
        )}
      </div>

      <Card>
        <CardHeader icon={RefreshCw} title="Onboarding Progress" />
        <CardBody>
          {(current === 'REJECTED' || current === 'SUSPENDED') && (
            <div style={{
              margin: '0 0 16px',
              background: '#FEF2F2',
              borderRadius: 10,
              padding: '14px 16px',
              border: '1px solid #FECACA',
            }}>
              <p style={{ margin: '0 0 4px', fontSize: 13, fontWeight: 700, color: '#991B1B' }}>
                {current === 'REJECTED' ? 'Application rejected' : 'Account suspended'}
              </p>
              <p style={{ margin: 0, fontSize: 12, color: '#991B1B', lineHeight: 1.5 }}>
                {current === 'REJECTED'
                  ? 'This application was rejected during onboarding. Review the audit trail for details.'
                  : 'This account has been suspended. Review the audit trail for details.'}
              </p>
            </div>
          )}

          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            marginBottom: 20,
            overflowX: isMobile ? 'auto' : 'visible',
          }}>
            {steps.map((step, i) => {
              const isDone = i < currentStepIdx;
              const isActive = i === currentStepIdx;

              return (
                <div key={step.key} style={{
                  display: 'flex',
                  alignItems: 'center',
                  flex: 1,
                  minWidth: isMobile ? 64 : 'auto',
                }}>
                  <div style={{ textAlign: 'center', flex: 1 }}>
                    <div style={{
                      width: isMobile ? 26 : 30,
                      height: isMobile ? 26 : 30,
                      borderRadius: 999,
                      margin: '0 auto 6px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: isDone || isActive ? '#F8FAFC' : theme.bgInput,
                      color: isDone ? '#166534' : isActive ? '#1D4ED8' : theme.textTertiary,
                      border: `1px solid ${isDone ? '#DCFCE7' : isActive ? '#DBEAFE' : theme.border}`,
                      fontWeight: 600,
                      fontSize: isMobile ? 10 : 12,
                    }}>
                      {isDone ? <CheckCircle size={isMobile ? 11 : 14} /> : i + 1}
                    </div>
                    <p style={{
                      fontSize: isMobile ? 10 : 11,
                      margin: 0,
                      fontWeight: isActive ? 600 : 500,
                      color: isDone ? '#166534' : isActive ? '#1D4ED8' : theme.textTertiary,
                    }}>
                      {step.label}
                    </p>
                  </div>

                  {i < steps.length - 1 && (
                    <div style={{
                      height: 1,
                      flex: 0.4,
                      marginBottom: isMobile ? 16 : 20,
                      background: isDone ? '#DCFCE7' : theme.border,
                    }} />
                  )}
                </div>
              );
            })}
          </div>

          {['REJECTED', 'GDPR_ERASED'].includes(current) ? (
            <div style={{
              background: current === 'REJECTED' ? '#FEF2F2' : theme.bgInput,
              borderRadius: 8,
              padding: '11px 14px',
              border: `1px solid ${current === 'REJECTED' ? '#FECACA' : theme.border}`,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}>
              {current === 'REJECTED'
                ? <Ban size={14} color="#991B1B" />
                : <Trash2 size={14} color={theme.textSecondary} />}
              <p style={{
                color: current === 'REJECTED' ? '#991B1B' : theme.textSecondary,
                fontWeight: 600,
                fontSize: 12,
                margin: 0,
              }}>
                This investor has been {current.toLowerCase().replace('_', ' ')}.
              </p>
            </div>
          ) : (
            <div>
              <p style={{
                fontSize: 11,
                color: theme.textTertiary,
                margin: '0 0 10px',
                fontWeight: 600,
              }}>
                Move to next status
              </p>

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {nextStatuses.map(s => (
                  <Btn
                    key={s}
                    onClick={() => handleStatusChange(s)}
                    disabled={updating}
                    variant={s === 'REJECTED' || s === 'SUSPENDED' ? 'danger' : 'primary'}
                  >
                    {updating ? '...' : s.replace(/_/g, ' ')}
                  </Btn>
                ))}
              </div>

              {updateMsg && (
                <div style={{
                  marginTop: 10,
                  padding: '9px 13px',
                  borderRadius: 8,
                  fontSize: 12,
                  fontWeight: 600,
                  background: updateMsg.type === 'success' ? '#F0FDF4' : '#FEF2F2',
                  border: `1px solid ${updateMsg.type === 'success' ? '#DCFCE7' : '#FECACA'}`,
                  color: updateMsg.type === 'success' ? '#166534' : '#991B1B',
                }}>
                  {updateMsg.text}
                </div>
              )}
            </div>
          )}
        </CardBody>
      </Card>

      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
        gap: 14,
      }}>
        <Card style={{ marginBottom: isMobile ? 0 : 14 }}>
          <CardHeader icon={User} title="Personal Information" />
          <CardBody>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 20px' }}>
              <Field label="Email" value={inv?.email} />
              <Field label="Nationality" value={inv?.nationality} />
              <Field label="Country" value={inv?.address_country} />
              <Field label="Investor Type" value={inv?.investor_type} />
              <Field label="Language" value={inv?.preferred_language} />
              <Field
                label="Created"
                value={inv?.created_at
                  ? new Date(inv.created_at).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })
                  : null}
              />
            </div>
          </CardBody>
        </Card>

        <Card style={{ marginBottom: 14 }}>
          <CardHeader icon={Search} title="KYC Status" />
          <CardBody>
            {kyc ? (
              <>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '12px 14px',
                  borderRadius: 10,
                  marginBottom: 14,
                  background: theme.bgInput,
                  border: `1px solid ${theme.border}`,
                }}>
                  {kyc.kyc_result === 'PASS'
                    ? <CheckCircle size={18} color="#166534" />
                    : kyc.kyc_result === 'FAIL'
                      ? <XCircle size={18} color="#991B1B" />
                      : <Clock size={18} color="#92400E" />}
                  <div>
                    <StatusPill
                      value={kyc.kyc_result}
                      colorMap={{
                        PASS: { color: '#166534', bg: '#F8FAFC', border: '#DCFCE7', label: 'Pass' },
                        FAIL: { color: '#991B1B', bg: '#FEF2F2', border: '#FECACA', label: 'Fail' },
                        PENDING: { color: '#92400E', bg: '#FFFBEB', border: '#FDE68A', label: 'Pending' },
                      }}
                    />
                    <p style={{ margin: '6px 0 0', fontSize: 11, color: theme.textSecondary }}>
                      KYC verification result
                    </p>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px' }}>
                  <Field label="Document Type" value={kyc.document_type} />
                  <Field label="Issued Country" value={kyc.document_country} />
                  <Field
                    label="Verified at"
                    value={kyc.verified_at
                      ? new Date(kyc.verified_at).toLocaleDateString('en-GB', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                        })
                      : null}
                  />
                </div>
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '28px 0', color: theme.textTertiary }}>
                <Clock size={26} color={theme.textTertiary} style={{ marginBottom: 8 }} />
                <p style={{ fontSize: 12, margin: 0 }}>No KYC verification on record yet.</p>
              </div>
            )}
          </CardBody>
        </Card>
      </div>

      {(() => {
        const sub = audit.find(e => e.action === 'ONBOARDING_FORM_SUBMITTED');
        if (!sub) return null;

        const r = sub.reasoning || {};
        const s = r.suitability_answers || {};
        const amt = r.investment_amount;

        return (
          <Card>
            <CardHeader icon={FileText} title="Onboarding Submission" />
            <CardBody>
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(3, 1fr)',
                gap: '14px 20px',
              }}>
                <Field label="Investment Experience" value={s.investment_experience?.replace(/_/g, ' ')} />
                <Field label="Risk Tolerance" value={s.risk_tolerance?.replace(/_/g, ' ')} />
                <Field label="Investment Horizon" value={s.investment_horizon?.replace(/_/g, ' ')} />
                <Field label="Loss Tolerance" value={s.loss_tolerance?.replace(/_/g, ' ')} />
                <Field label="Investment Amount" value={amt ? `€${parseFloat(amt).toLocaleString('en-LU')}` : null} />
                <Field
                  label="Submitted at"
                  value={new Date(sub.timestamp).toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                />
              </div>
            </CardBody>
          </Card>
        );
      })()}

{suitability && (
        <Card>
          <CardHeader
            icon={BarChart2}
            title="Suitability Assessment"
            action={suitability.result && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <Btn onClick={fetchTranscript}>
                  <MessageSquare size={12} /> Transcript
                </Btn>
                {suitability.assessment_id && (
                  <Btn onClick={() => setShowOverride(true)}>
                    <AlertTriangle size={12} /> Override
                  </Btn>
                )}
              </div>
            )}
          />

          <CardBody>
            {suitability.result ? (
              <div style={{
                display: 'flex',
                alignItems: isMobile ? 'flex-start' : 'center',
                justifyContent: 'space-between',
                flexDirection: isMobile ? 'column' : 'row',
                gap: 14,
                marginBottom: 16,
              }}>
                <div>
                  <StatusPill value={suitability.result} colorMap={resultMeta} />
                  <p style={{ margin: '8px 0 0', fontSize: 12, color: theme.textSecondary }}>
                    MiFID II suitability determination
                  </p>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  {shapScore !== null && (
                    <div style={{
                      border: `1px solid ${theme.border}`,
                      background: theme.bgInput,
                      borderRadius: 10,
                      padding: '8px 12px',
                      minWidth: 92,
                    }}>
                      <p style={{ margin: 0, fontSize: 18, fontWeight: 700, color: theme.textPrimary }}>
                        {shapScore}<span style={{ fontSize: 11, color: theme.textTertiary }}>/100</span>
                      </p>
                      <p style={{ margin: '2px 0 0', fontSize: 10, color: theme.textTertiary }}>Score</p>
                    </div>
                  )}

                  {suitability.recommended_action && (
                    <StatusPill value={suitability.recommended_action.replace(/_/g, ' ')} colorMap={actionMeta} />
                  )}
                </div>
              </div>
            ) : (
              <div style={{
                background: theme.bgInput,
                borderRadius: 8,
                border: `1px solid ${theme.border}`,
                padding: '11px 14px',
                marginBottom: 14,
                fontSize: 12,
                color: theme.textSecondary,
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: 7,
              }}>
                <Clock size={13} /> The investor is currently completing their suitability assessment.
              </div>
            )}

            <div style={{ height: 1, background: theme.border, margin: '14px 0' }} />

            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
              gap: '12px 16px',
              marginBottom: 16,
            }}>
              <Field label="Risk Profile" value={suitability.risk_profile} />
              <Field label="Investment Horizon" value={suitability.investment_horizon} />
              <Field
                label="Started"
                value={suitability.started_at
                  ? new Date(suitability.started_at).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                  : null}
              />
              <Field
                label="Completed"
                value={suitability.completed_at
                  ? new Date(suitability.completed_at).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                  : null}
              />
            </div>

            {suitability.status === 'COMPLETED' && (
              <>
                <p style={{ fontSize: 11, color: theme.textTertiary, fontWeight: 600, margin: '0 0 8px' }}>
                  Inconsistency detection
                </p>

                <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
                  {[
                    { label: 'High', count: suitability.inconsistency_count_high ?? 0, tone: 'danger' },
                    { label: 'Medium', count: suitability.inconsistency_count_medium ?? 0, tone: 'warning' },
                    { label: 'Low', count: suitability.inconsistency_count_low ?? 0, tone: 'neutral' },
                  ].map(t => (
                    <div key={t.label} style={{
                      background: theme.bgInput,
                      border: `1px solid ${theme.border}`,
                      borderRadius: 8,
                      padding: '8px 12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: theme.textSecondary }}>{t.label}</span>
                      <span style={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: t.tone === 'danger' ? '#991B1B' : t.tone === 'warning' ? '#92400E' : theme.textPrimary,
                      }}>
                        {t.count}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}

            {suitability.shap_values && (() => {
              const s = suitability.shap_values;
              const pen = Math.abs(s.inconsistency_penalty || 0);
              const factors = [
                { label: 'Knowledge & Experience', key: 'knowledge_and_experience', max: 0.30 },
                { label: 'Financial Situation', key: 'financial_situation', max: 0.35 },
                { label: 'Investment Objectives', key: 'investment_objectives', max: 0.30 },
                { label: 'ESG Alignment', key: 'esg_alignment', max: 0.15 },
              ];

              return (
                <div style={{ marginBottom: 16 }}>
                  <p style={{ fontSize: 11, color: theme.textTertiary, fontWeight: 600, margin: '0 0 10px' }}>
                    Factor breakdown
                  </p>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {factors.map(f => {
                      const val = s[f.key] || 0;
                      const pct = Math.round((val / f.max) * 100);

                      return (
                        <div key={f.key}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                            <span style={{ fontSize: 12, color: theme.textSecondary, fontWeight: 500 }}>{f.label}</span>
                            <span style={{ fontSize: 11, color: theme.textTertiary, fontWeight: 600 }}>
                              {Math.round(val * 100)} / {Math.round(f.max * 100)}
                            </span>
                          </div>
                          <div style={{ background: theme.bgInput, borderRadius: 999, height: 6, border: `1px solid ${theme.border}` }}>
                            <div style={{
                              width: `${Math.min(100, pct)}%`,
                              background: '#2563EB',
                              borderRadius: 999,
                              height: 4,
                              margin: 1,
                            }} />
                          </div>
                        </div>
                      );
                    })}

                    {pen > 0 && (
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                          <span style={{ fontSize: 12, color: '#991B1B', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 5 }}>
                            <AlertTriangle size={12} /> Inconsistency penalty
                          </span>
                          <span style={{ fontSize: 11, color: '#991B1B', fontWeight: 700 }}>
                            -{Math.round(pen * 100)}
                          </span>
                        </div>
                        <div style={{ background: theme.bgInput, borderRadius: 999, height: 6, border: `1px solid ${theme.border}` }}>
                          <div style={{
                            width: `${Math.min(100, pen * 200)}%`,
                            background: '#991B1B',
                            borderRadius: 999,
                            height: 4,
                            margin: 1,
                          }} />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {suitability.reasoning && (() => {
              const isOverride = suitability.reasoning.startsWith('[COMPLIANCE OFFICER OVERRIDE]');
              const overrideParts = isOverride
                ? suitability.reasoning.match(/\[COMPLIANCE OFFICER OVERRIDE\] Previous AI result: (\w+)\. Override reason: (.+)/)
                : null;

              return (
                <div style={{ border: `1px solid ${theme.border}`, borderRadius: 8, overflow: 'hidden', marginBottom: 10 }}>
                  <button onClick={() => setShowReasoning(v => !v)} style={{
                    width: '100%',
                    padding: '11px 14px',
                    background: theme.bgInput,
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}>
                    <span style={{ fontSize: 11, fontWeight: 600, color: theme.textSecondary, display: 'flex', alignItems: 'center', gap: 6 }}>
                      {isOverride ? <><AlertTriangle size={12} /> Compliance officer override</> : <><Bot size={12} /> AI reasoning</>}
                    </span>
                    {showReasoning ? <ChevronUp size={13} color={theme.textSecondary} /> : <ChevronDown size={13} color={theme.textSecondary} />}
                  </button>

                  {showReasoning && (
                    <div style={{ padding: '12px 14px', borderTop: `1px solid ${theme.border}` }}>
                      {isOverride && overrideParts ? (
                        <>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px', marginBottom: 12 }}>
                            <Field label="Previous AI Result" value={resultMeta[overrideParts[1]]?.label || overrideParts[1]} />
                            <Field label="New Result" value={resultMeta[suitability.result]?.label || suitability.result} />
                          </div>
                          <p style={{ margin: '0 0 5px', fontSize: 11, color: theme.textTertiary, fontWeight: 600 }}>
                            Officer justification
                          </p>
                          <p style={{ margin: 0, fontSize: 13, color: theme.textSecondary, lineHeight: 1.7 }}>
                            {overrideParts[2]}
                          </p>
                        </>
                      ) : (
                        <p style={{ margin: 0, fontSize: 13, color: theme.textSecondary, lineHeight: 1.7 }}>
                          {suitability.reasoning}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}

            {suitability.shap_plain_language && (
              <div style={{ border: `1px solid ${theme.border}`, borderRadius: 8, overflow: 'hidden' }}>
                <button onClick={() => setShowNarrative(v => !v)} style={{
                  width: '100%',
                  padding: '11px 14px',
                  background: theme.bgInput,
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: theme.textSecondary, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Info size={12} /> Confidence explanation
                  </span>
                  {showNarrative ? <ChevronUp size={13} color={theme.textSecondary} /> : <ChevronDown size={13} color={theme.textSecondary} />}
                </button>

                {showNarrative && (
                  <div style={{ padding: '12px 14px', borderTop: `1px solid ${theme.border}` }}>
                    <p style={{ margin: 0, fontSize: 13, color: theme.textSecondary, lineHeight: 1.7 }}>
                      {suitability.shap_plain_language}
                    </p>
                  </div>
                )}
              </div>
            )}
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader
          icon={Shield}
          title="AML Risk Assessment"
          action={amlRating && (
            <Btn onClick={handleRunAml} disabled={amlRunning}>
              {amlRunning ? <><Clock size={12} /> Running...</> : <><RotateCcw size={12} /> Re-run</>}
            </Btn>
          )}
        />

        <CardBody>
          {amlRating ? (
            <>
              <div style={{
                display: 'flex',
                alignItems: isMobile ? 'flex-start' : 'center',
                justifyContent: 'space-between',
                flexDirection: isMobile ? 'column' : 'row',
                gap: 12,
                marginBottom: 16,
              }}>
                <div>
                  <StatusPill
                    value={`${amlRating.overall_aml_risk} RISK`}
                    colorMap={{
                      'LOW RISK': { color: '#166534', bg: '#F8FAFC', border: '#DCFCE7', label: 'Low Risk' },
                      'MEDIUM RISK': { color: '#92400E', bg: '#FFFBEB', border: '#FDE68A', label: 'Medium Risk' },
                      'HIGH RISK': { color: '#991B1B', bg: '#FEF2F2', border: '#FECACA', label: 'High Risk' },
                    }}
                  />
                  <p style={{ margin: '8px 0 0', fontSize: 12, color: theme.textSecondary }}>
                    Overall AML risk rating
                  </p>
                </div>

                {amlRating.recommended_action && (
                  <StatusPill value={amlRating.recommended_action.replace(/_/g, ' ')} colorMap={actionMeta} />
                )}
              </div>

              <div style={{ height: 1, background: theme.border, margin: '14px 0' }} />

              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
                gap: 10,
                marginBottom: 16,
              }}>
                {[
                  { label: 'Geographic Risk', value: amlRating.geographic_risk || amlRating.network_risk },
                  { label: 'Behavioural Risk', value: amlRating.behavioural_score },
                  { label: 'Amount Risk', value: amlRating.amount_risk },
                ].map(d => (
                  <div key={d.label} style={{
                    background: theme.bgInput,
                    border: `1px solid ${theme.border}`,
                    borderRadius: 9,
                    padding: '12px 14px',
                  }}>
                    <p style={{ margin: '0 0 5px', fontSize: 11, color: theme.textTertiary, fontWeight: 600 }}>
                      {d.label}
                    </p>
                    <p style={{ margin: 0, fontWeight: 600, fontSize: 14, color: theme.textPrimary }}>
                      {d.value || '-'}
                    </p>
                  </div>
                ))}
              </div>

              {amlRating.red_flags?.length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <p style={{
                    fontSize: 11,
                    color: theme.textTertiary,
                    fontWeight: 600,
                    margin: '0 0 8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 5,
                  }}>
                    <AlertTriangle size={11} /> Red flags detected
                  </p>

                  {amlRating.red_flags.map((flag, i) => (
                    <div key={`flag-${i}`} style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 10,
                      padding: '10px 12px',
                      borderRadius: 8,
                      marginBottom: 6,
                      background: flag.severity === 'HIGH' ? '#FEF2F2' : theme.bgInput,
                      border: `1px solid ${flag.severity === 'HIGH' ? '#FECACA' : theme.border}`,
                    }}>
                      <span style={{
                        width: 6,
                        height: 6,
                        borderRadius: 999,
                        marginTop: 5,
                        flexShrink: 0,
                        background: flag.severity === 'HIGH' ? '#991B1B' : '#92400E',
                      }} />
                      <div>
                        <span style={{ fontSize: 11, fontWeight: 600, color: theme.textSecondary }}>
                          {flag.severity} · {flag.category}
                        </span>
                        <p style={{ margin: '3px 0 0', fontSize: 12, color: theme.textPrimary, lineHeight: 1.5 }}>
                          {flag.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {amlRating.risk_narrative && (
                <div style={{ border: `1px solid ${theme.border}`, borderRadius: 8, overflow: 'hidden' }}>
                  <button onClick={() => setShowNarrative(v => !v)} style={{
                    width: '100%',
                    padding: '11px 14px',
                    background: theme.bgInput,
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}>
                    <span style={{ fontSize: 11, fontWeight: 600, color: theme.textSecondary, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <ClipboardList size={12} /> Risk narrative
                    </span>
                    {showNarrative ? <ChevronUp size={13} color={theme.textSecondary} /> : <ChevronDown size={13} color={theme.textSecondary} />}
                  </button>

                  {showNarrative && (
                    <div style={{ padding: '12px 14px', borderTop: `1px solid ${theme.border}` }}>
                      <p style={{ margin: 0, fontSize: 13, color: theme.textSecondary, lineHeight: 1.7 }}>
                        {amlRating.risk_narrative}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '32px 0' }}>
              <Shield size={30} color={theme.textTertiary} style={{ marginBottom: 10 }} />
              <p style={{ fontSize: 13, color: theme.textSecondary, margin: '0 0 16px', fontWeight: 500 }}>
                No AML analysis has been run yet.
              </p>
              <Btn onClick={handleRunAml} disabled={amlRunning} size="md" variant="primary">
                {amlRunning ? <><Clock size={13} /> Running analysis...</> : <><Shield size={13} /> Run AML Analysis</>}
              </Btn>
            </div>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          icon={Mail}
          title="Document Delivery"
          action={(
            <Btn onClick={handleSendDocuments} disabled={delivering || !suitability?.result} variant="primary">
              {delivering ? <><Clock size={12} /> Sending...</> : <><Mail size={12} /> Send Documents</>}
            </Btn>
          )}
        />

        <CardBody>
          {deliverMsg && (
            <div style={{
              padding: '10px 14px',
              borderRadius: 8,
              marginBottom: 14,
              fontSize: 13,
              fontWeight: 600,
              background: deliverMsg.type === 'success' ? '#F0FDF4' : '#FEF2F2',
              border: `1px solid ${deliverMsg.type === 'success' ? '#DCFCE7' : '#FECACA'}`,
              color: deliverMsg.type === 'success' ? '#166534' : '#991B1B',
            }}>
              {deliverMsg.text}
            </div>
          )}

          {deliveries.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <Mail size={28} color={theme.textTertiary} style={{ marginBottom: 8 }} />
              <p style={{ fontSize: 13, color: theme.textSecondary, margin: '0 0 4px', fontWeight: 500 }}>
                No documents sent yet
              </p>
              <p style={{ fontSize: 12, color: theme.textTertiary, margin: 0 }}>
                {!suitability?.result ? 'Suitability must be completed first.' : 'Click Send Documents to deliver.'}
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {deliveries.map(d => (
                <div key={d.id} style={{
                  background: theme.bgInput,
                  borderRadius: 8,
                  padding: '12px 14px',
                  border: `1px solid ${theme.border}`,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                    <div>
                      <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: theme.textPrimary }}>
                        {d.acknowledged_at ? 'Acknowledged' : 'Awaiting acknowledgement'}
                      </p>
                      <p style={{ margin: '3px 0 0', fontSize: 11, color: theme.textSecondary }}>
                        Sent {new Date(d.delivered_at).toLocaleDateString('en-GB', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </p>
                    </div>

                    <StatusPill
                      value={d.acknowledged_at ? 'Confirmed' : 'Pending'}
                      colorMap={{
                        Confirmed: { color: '#166534', bg: '#F8FAFC', border: '#DCFCE7', label: 'Confirmed' },
                        Pending: { color: '#92400E', bg: '#FFFBEB', border: '#FDE68A', label: 'Pending' },
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      {!['GDPR_ERASED'].includes(current) && (
        <Card>
          <CardHeader icon={Shield} title="Account Management" />
          <CardBody>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {current === 'SUSPENDED' ? (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px 14px',
                  background: '#FFFBEB',
                  borderRadius: 8,
                  border: '1px solid #FDE68A',
                }}>
                  <div>
                    <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#92400E' }}>
                      Account suspended
                    </p>
                    <p style={{ margin: '2px 0 0', fontSize: 11, color: '#92400E' }}>
                      Reactivate to restore investor access.
                    </p>
                  </div>
                  <Btn onClick={handleReactivate} disabled={suspending} variant="primary">
                    {suspending ? '...' : 'Reactivate'}
                  </Btn>
                </div>
              ) : !['REJECTED'].includes(current) && (
                <div>
                  {!showSuspend ? (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '12px 14px',
                      background: theme.bgInput,
                      borderRadius: 8,
                      border: `1px solid ${theme.border}`,
                      gap: 12,
                    }}>
                      <div>
                        <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: theme.textPrimary }}>
                          Suspend account
                        </p>
                        <p style={{ margin: '2px 0 0', fontSize: 11, color: theme.textSecondary }}>
                          Temporarily block investor access. Reversible.
                        </p>
                      </div>
                      <Btn onClick={() => setShowSuspend(true)} variant="danger">Suspend</Btn>
                    </div>
                  ) : (
                    <div style={{
                      padding: 14,
                      background: '#FEF2F2',
                      borderRadius: 8,
                      border: '1px solid #FECACA',
                    }}>
                      <p style={{ margin: '0 0 10px', fontSize: 13, fontWeight: 700, color: '#991B1B' }}>
                        Suspend account
                      </p>
                      <input
                        type="text"
                        placeholder="Reason for suspension (min 10 chars)"
                        value={suspendReason}
                        onChange={e => setSuspendReason(e.target.value)}
                        style={{ ...inputStyle, borderColor: '#FECACA', marginBottom: 10 }}
                      />
                      {suspendMsg && (
                        <p style={{
                          margin: '0 0 10px',
                          fontSize: 12,
                          color: suspendMsg.type === 'error' ? '#991B1B' : '#166534',
                          fontWeight: 600,
                        }}>
                          {suspendMsg.text}
                        </p>
                      )}
                      <div style={{ display: 'flex', gap: 8 }}>
                        <Btn onClick={() => { setShowSuspend(false); setSuspendReason(''); setSuspendMsg(null); }}>
                          Cancel
                        </Btn>
                        <Btn
                          onClick={handleSuspend}
                          disabled={suspending || suspendReason.trim().length < 10}
                          variant="danger"
                        >
                          {suspending ? 'Suspending...' : 'Confirm'}
                        </Btn>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div>
                {!showGdpr ? (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 14px',
                    background: theme.bgInput,
                    borderRadius: 8,
                    border: `1px solid ${theme.border}`,
                    gap: 12,
                  }}>
                    <div>
                      <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: theme.textPrimary }}>
                        GDPR erasure
                      </p>
                      <p style={{ margin: '2px 0 0', fontSize: 11, color: theme.textSecondary }}>
                        Permanently erase all PII. Irreversible. Audit trail preserved.
                      </p>
                    </div>
                    <Btn onClick={() => setShowGdpr(true)} variant="danger">Erase Data</Btn>
                  </div>
                ) : (
                  <div style={{
                    padding: 14,
                    background: '#FEF2F2',
                    borderRadius: 8,
                    border: '1px solid #FECACA',
                  }}>
                    <p style={{ margin: '0 0 6px', fontSize: 13, fontWeight: 700, color: '#991B1B' }}>
                      Permanent GDPR erasure
                    </p>
                    <p style={{ margin: '0 0 12px', fontSize: 12, color: '#991B1B', lineHeight: 1.5 }}>
                      This will permanently erase all personal data. The audit trail will be preserved as required by MiFID II. This action cannot be undone.
                    </p>
                    <p style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 600, color: theme.textPrimary }}>
                      Type <code style={{ background: '#FEE2E2', padding: '1px 5px', borderRadius: 4 }}>{inv.pseudo_id}</code> to confirm:
                    </p>
                    <input
                      type="text"
                      placeholder={inv.pseudo_id}
                      value={gdprConfirm}
                      onChange={e => setGdprConfirm(e.target.value)}
                      style={{ ...inputStyle, borderColor: '#FECACA', marginBottom: 12 }}
                    />
                    {gdprMsg && (
                      <p style={{
                        margin: '0 0 10px',
                        fontSize: 12,
                        color: gdprMsg.type === 'error' ? '#991B1B' : '#166534',
                        fontWeight: 600,
                      }}>
                        {gdprMsg.text}
                      </p>
                    )}
                    <div style={{ display: 'flex', gap: 8 }}>
                      <Btn onClick={() => { setShowGdpr(false); setGdprConfirm(''); setGdprMsg(null); }}>
                        Cancel
                      </Btn>
                      <Btn
                        onClick={handleGdprErasure}
                        disabled={gdprLoading || gdprConfirm !== inv.pseudo_id}
                        variant="danger"
                      >
                        {gdprLoading ? 'Erasing...' : 'Confirm Erasure'}
                      </Btn>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader icon={Link2} title={`Audit Trail - ${audit.length} events`} />
        <CardBody style={{ padding: 0 }}>
          {audit.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px 0', color: theme.textTertiary }}>
              <ClipboardList size={28} color={theme.textTertiary} style={{ marginBottom: 8 }} />
              <p style={{ fontSize: 12, margin: 0 }}>No audit events yet.</p>
            </div>
          ) : (
            <div style={{ maxHeight: 360, overflowY: 'auto' }}>
              {audit.map((entry, idx) => (
                <div key={entry.record_id || `audit-${idx}`} style={{
                  display: 'flex',
                  gap: 12,
                  padding: isMobile ? '12px 16px' : '13px 20px',
                  borderBottom: idx < audit.length - 1 ? `1px solid ${theme.border}` : 'none',
                }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                    <div style={{
                      width: 7,
                      height: 7,
                      borderRadius: 999,
                      background: theme.textTertiary,
                      marginTop: 5,
                      flexShrink: 0,
                    }} />
                    {idx < audit.length - 1 && (
                      <div style={{
                        width: 1,
                        flex: 1,
                        minHeight: 16,
                        background: theme.border,
                        marginTop: 4,
                      }} />
                    )}
                  </div>

                  <div style={{ flex: 1, paddingBottom: 4 }}>
                    <p style={{ margin: 0, fontWeight: 600, fontSize: 12, color: theme.textPrimary }}>
                      {entry.action.replace(/_/g, ' ')}
                    </p>
                    <div style={{ display: 'flex', gap: 6, marginTop: 5, alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={{
                        fontSize: 10,
                        color: theme.textSecondary,
                        background: theme.bgInput,
                        border: `1px solid ${theme.border}`,
                        padding: '2px 7px',
                        borderRadius: 999,
                        fontWeight: 600,
                      }}>
                        {entry.actor}
                      </span>
                      <span style={{ fontSize: 10, color: theme.textTertiary }}>
                        {new Date(entry.timestamp).toLocaleDateString('en-GB', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                      {entry.module && (
                        <span style={{
                          fontSize: 10,
                          color: theme.textSecondary,
                          background: theme.bgInput,
                          border: `1px solid ${theme.border}`,
                          padding: '2px 7px',
                          borderRadius: 999,
                          fontWeight: 600,
                        }}>
                          {entry.module}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      {showOverride && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: theme.bgOverlay,
          display: 'flex',
          alignItems: isMobile ? 'flex-end' : 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            background: theme.bgCard,
            borderRadius: isMobile ? '16px 16px 0 0' : 12,
            width: '100%',
            maxWidth: isMobile ? '100%' : 520,
            maxHeight: isMobile ? '90vh' : 'auto',
            overflowY: 'auto',
            boxSizing: 'border-box',
            boxShadow: theme.shadowLg,
            border: isMobile ? 'none' : `1px solid ${theme.border}`,
          }}>
            <div style={{
              padding: '18px 20px',
              borderBottom: `1px solid ${theme.border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <h2 style={{
                margin: 0,
                fontSize: 15,
                fontWeight: 700,
                color: theme.textPrimary,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}>
                <AlertTriangle size={16} color="#92400E" /> Compliance Officer Override
              </h2>
              <button onClick={() => { setShowOverride(false); setOverrideMsg(null); setOverrideReason(''); }} style={{
                background: theme.bgInput,
                border: `1px solid ${theme.border}`,
                borderRadius: 8,
                cursor: 'pointer',
                padding: 8,
                display: 'flex',
                color: theme.textSecondary,
              }}>
                <X size={16} />
              </button>
            </div>

            <div style={{ padding: 20 }}>
              <div style={{
                background: theme.bgInput,
                borderRadius: 8,
                padding: '12px 14px',
                marginBottom: 18,
                border: `1px solid ${theme.border}`,
              }}>
                <Field label="Current AI Result" value={resultMeta[suitability?.result]?.label || suitability?.result} />
              </div>

              <div style={{ marginBottom: 16 }}>
                <p style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 600, color: theme.textSecondary }}>
                  New result
                </p>
                <div style={{ display: 'flex', gap: 8 }}>
                  {['SUITABLE', 'BORDERLINE', 'UNSUITABLE'].map(r => {
                    const selected = overrideResult === r;
                    return (
                      <button key={r} onClick={() => setOverrideResult(r)} style={{
                        flex: 1,
                        padding: '10px 8px',
                        borderRadius: 8,
                        cursor: 'pointer',
                        fontWeight: 600,
                        fontSize: 12,
                        background: selected ? resultMeta[r]?.bg : theme.bgInput,
                        color: selected ? resultMeta[r]?.color : theme.textSecondary,
                        border: `1px solid ${selected ? resultMeta[r]?.border : theme.border}`,
                      }}>
                        {resultMeta[r]?.label || r}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div style={{ marginBottom: 16 }}>
                <p style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 600, color: theme.textSecondary }}>
                  Written justification <span style={{ color: '#991B1B' }}>*</span>
                </p>
                <textarea
                  value={overrideReason}
                  onChange={e => setOverrideReason(e.target.value)}
                  placeholder="Provide a written justification (minimum 20 characters)."
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: 8,
                    border: `1px solid ${theme.border}`,
                    fontSize: 13,
                    resize: 'vertical',
                    outline: 'none',
                    fontFamily: 'inherit',
                    color: theme.textPrimary,
                    lineHeight: 1.5,
                    boxSizing: 'border-box',
                    background: theme.bgInput,
                  }}
                />
                <p style={{
                  margin: '5px 0 0',
                  fontSize: 11,
                  color: overrideReason.length < 20 ? theme.textTertiary : '#166534',
                }}>
                  {overrideReason.length} / 20 characters minimum
                </p>
              </div>

              <div style={{
                background: '#FFFBEB',
                borderRadius: 8,
                border: '1px solid #FDE68A',
                padding: '10px 14px',
                marginBottom: 16,
                display: 'flex',
                gap: 10,
                alignItems: 'flex-start',
              }}>
                <AlertTriangle size={14} color="#92400E" style={{ marginTop: 1, flexShrink: 0 }} />
                <p style={{ margin: 0, fontSize: 12, color: '#92400E', lineHeight: 1.5 }}>
                  This override will be permanently recorded in the immutable audit log.
                </p>
              </div>

              {overrideMsg && (
                <div style={{
                  marginBottom: 14,
                  padding: '10px 14px',
                  borderRadius: 8,
                  fontSize: 12,
                  fontWeight: 600,
                  background: overrideMsg.type === 'success' ? '#F0FDF4' : '#FEF2F2',
                  border: `1px solid ${overrideMsg.type === 'success' ? '#DCFCE7' : '#FECACA'}`,
                  color: overrideMsg.type === 'success' ? '#166534' : '#991B1B',
                }}>
                  {overrideMsg.text}
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <Btn onClick={() => { setShowOverride(false); setOverrideMsg(null); setOverrideReason(''); }}>
                  Cancel
                </Btn>
                <Btn onClick={handleOverride} disabled={overriding || overrideReason.trim().length < 20} variant="primary">
                  {overriding ? <><Clock size={12} /> Recording...</> : <><CheckCircle size={12} /> Confirm Override</>}
                </Btn>
              </div>
            </div>
          </div>
        </div>
      )}

      {showTranscript && transcript && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: theme.bgOverlay,
          display: 'flex',
          alignItems: isMobile ? 'flex-end' : 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            background: theme.bgCard,
            borderRadius: isMobile ? '16px 16px 0 0' : 12,
            width: '100%',
            maxWidth: isMobile ? '100%' : 680,
            maxHeight: isMobile ? '92vh' : '82vh',
            overflowY: 'auto',
            boxSizing: 'border-box',
            boxShadow: theme.shadowLg,
            border: isMobile ? 'none' : `1px solid ${theme.border}`,
          }}>
            <div style={{
              padding: '18px 20px',
              borderBottom: `1px solid ${theme.border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              position: 'sticky',
              top: 0,
              background: theme.bgCard,
              zIndex: 1,
            }}>
              <h2 style={{
                margin: 0,
                fontSize: 15,
                fontWeight: 700,
                color: theme.textPrimary,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}>
                <MessageSquare size={16} color={theme.textSecondary} /> Suitability Conversation
              </h2>
              <button onClick={() => setShowTranscript(false)} style={{
                background: theme.bgInput,
                border: `1px solid ${theme.border}`,
                borderRadius: 8,
                cursor: 'pointer',
                padding: 8,
                display: 'flex',
                color: theme.textSecondary,
              }}>
                <X size={16} />
              </button>
            </div>

            <div style={{
              padding: '12px 18px',
              background: theme.bgInput,
              borderBottom: `1px solid ${theme.border}`,
              display: 'flex',
              gap: 16,
              flexWrap: 'wrap',
              fontSize: 11,
              color: theme.textSecondary,
            }}>
              <span>Started: {new Date(transcript.session.started_at).toLocaleString()}</span>
              {transcript.session.completed_at && <span style={{ color: '#166534' }}>Completed</span>}
              <span>Language: {transcript.session.language}</span>
              {transcript.session.jailbreak_attempts > 0 && (
                <span style={{ color: '#991B1B' }}>{transcript.session.jailbreak_attempts} jailbreak attempt(s)</span>
              )}
            </div>

            <div style={{ padding: isMobile ? 16 : '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
              {transcript.exchanges.map((ex, idx) => (
                <div key={ex.exchange_number || `ex-${idx}`} style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: ex.role === 'AI' ? 'flex-start' : 'flex-end',
                }}>
                  <span style={{
                    fontSize: 10,
                    color: theme.textTertiary,
                    marginBottom: 4,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                  }}>
                    {ex.role === 'AI' ? <><Bot size={10} /> RegShield AI</> : <><User size={10} /> Investor</>}
                  </span>
                  <div style={{
                    maxWidth: '85%',
                    padding: '10px 14px',
                    borderRadius: ex.role === 'AI' ? '4px 12px 12px 12px' : '12px 4px 12px 12px',
                    background: ex.role === 'AI' ? theme.bgInput : '#2563EB',
                    border: ex.role === 'AI' ? `1px solid ${theme.border}` : '1px solid #2563EB',
                    color: ex.role === 'AI' ? theme.textPrimary : 'white',
                    fontSize: 13,
                    lineHeight: 1.65,
                  }}>
                    {ex.content}
                  </div>
                  <span style={{ fontSize: 10, color: theme.textTertiary, marginTop: 3 }}>
                    {new Date(ex.timestamp).toLocaleTimeString()}
                  </span>
                </div>
              ))}
            </div>

            <div style={{ padding: '14px 20px', borderTop: `1px solid ${theme.border}`, textAlign: 'center' }}>
              <Btn onClick={() => setShowTranscript(false)} size="md">Close</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvestorDetail;
